import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  applyDarkClass,
  parseThemePreference,
  readStoredThemePreference,
  resolveTheme,
  shouldApplyDarkClass,
  writeStoredThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from '@/lib/themePreference';

interface ThemeContextValue {
  preference: ThemePreference;
  theme: ResolvedTheme;
  toggleTheme: () => void;
  setTheme: (t: ResolvedTheme) => void;
  setPreference: (t: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function colorSchemeMedia(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia('(prefers-color-scheme: dark)');
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredThemePreference);
  const [userId, setUserId] = useState<string | null>(null);

  const media = colorSchemeMedia();
  const theme = resolveTheme(preference, media);

  const persistPreference = useCallback((next: ThemePreference, accountId: string | null) => {
    writeStoredThemePreference(next);
    if (!accountId) return;
    void supabase
      .from('user_ui_preferences')
      .upsert({ user_id: accountId, color_scheme: next }, { onConflict: 'user_id' })
      .then(({ error }) => {
        if (error) console.error('saving colour scheme failed', error);
      });
  }, []);

  const setPreference = useCallback(
    (next: ThemePreference) => {
      setPreferenceState(next);
      persistPreference(next, userId);
    },
    [persistPreference, userId],
  );

  const setTheme = useCallback((next: ResolvedTheme) => setPreference(next), [setPreference]);

  const toggleTheme = useCallback(() => {
    setPreference(theme === 'dark' ? 'light' : 'dark');
  }, [setPreference, theme]);

  useEffect(() => {
    applyDarkClass(shouldApplyDarkClass(preference, pathname, colorSchemeMedia()));
  }, [preference, pathname, theme]);

  useEffect(() => {
    writeStoredThemePreference(preference);
  }, [preference]);

  useEffect(() => {
    if (preference !== 'system') return;
    const mq = colorSchemeMedia();
    if (!mq) return;
    const onChange = () => {
      applyDarkClass(shouldApplyDarkClass('system', pathname, mq));
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference, pathname]);

  useEffect(() => {
    const applyForPrint = (enableDark: boolean) => {
      applyDarkClass(enableDark && shouldApplyDarkClass(preference, pathname, colorSchemeMedia()));
    };
    const onBeforePrint = () => applyDarkClass(false);
    const onAfterPrint = () => applyForPrint(true);
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, [preference, pathname]);

  useEffect(() => {
    let cancelled = false;

    const loadAccountPreference = async (id: string) => {
      const { data, error } = await supabase
        .from('user_ui_preferences')
        .select('color_scheme')
        .eq('user_id', id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error('loading colour scheme failed', error);
        return;
      }
      if (data?.color_scheme) {
        const next = parseThemePreference(data.color_scheme);
        setPreferenceState(next);
        writeStoredThemePreference(next);
      }
    };

    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const id = data.session?.user.id ?? null;
      setUserId(id);
      if (id) void loadAccountPreference(id);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const id = session?.user.id ?? null;
      setUserId(id);
      if (id) void loadAccountPreference(id);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, theme, toggleTheme, setTheme, setPreference }),
    [preference, theme, toggleTheme, setTheme, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}
